import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useLibraryPageController } from '../../../library';
import { useSeedanceLibraryStore } from '../../../../store/seedanceLibraryStore';
import { getLocalStorageValue, SEEDANCE_STORAGE_KEYS } from '../../../../utils/seedanceStorage';
import { AssetCard } from '../../../../components/AssetCard';
import { AudioAssetCard } from '../../../../components/AudioAssetCard';
import { VideoAssetCard } from '../../../../components/VideoAssetCard';
import { VirtualAvatarGroupSwitcher } from '../../../../components/VirtualAvatarGroupSwitcher';
import { LIBRARY_AUDIO_ACCEPT } from '../../../../utils/libraryAudioUpload';
import './libraryDrawer.css';

interface LibraryDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const LibraryDrawer: React.FC<LibraryDrawerProps> = ({ open, onClose }) => {
  const drawerScrollRef = useRef<HTMLDivElement | null>(null);
  const drawerLoadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  const hasTaskListConfig = useCallback(() => {
    const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
    return Boolean(userId);
  }, []);

  const libSelectedGroupId = useSeedanceLibraryStore((s) => s.libSelectedGroupId);
  const libUploading = useSeedanceLibraryStore((s) => s.libUploading);

  // Drawer-specific independent state
  const libDrawerRemoteAssets = useSeedanceLibraryStore((s) => s.libDrawerRemoteAssets);
  const libDrawerPortraitHasMore = useSeedanceLibraryStore((s) => s.libDrawerPortraitHasMore);
  const libDrawerPortraitLoading = useSeedanceLibraryStore((s) => s.libDrawerPortraitLoading);
  const libDrawerPortraitLoadingMore = useSeedanceLibraryStore((s) => s.libDrawerPortraitLoadingMore);
  const libDrawerSearchName = useSeedanceLibraryStore((s) => s.libDrawerSearchName);
  const setLibDrawerSearchName = useSeedanceLibraryStore((s) => s.setLibDrawerSearchName);

  const [nameSearchInput, setNameSearchInput] = useState(libDrawerSearchName);

  // Sync input when external clear happens (e.g. group change)
  useEffect(() => {
    if (!libDrawerSearchName) setNameSearchInput('');
  }, [libDrawerSearchName]);

  const submitNameSearch = useCallback(() => {
    setLibDrawerSearchName(nameSearchInput.trim());
  }, [nameSearchInput, setLibDrawerSearchName]);

  const clearNameSearch = useCallback(() => {
    setNameSearchInput('');
    setLibDrawerSearchName('');
  }, [setLibDrawerSearchName]);

  const {
    drawerUploadRef,
    uploadPortraitsToSelectedGroup,
    pickAssetFromDrawer,
    requestLibraryAssetDelete,
  } = useLibraryPageController({
    active: open,
    hasTaskListConfig,
    pagingLayout: 'drawer',
    scrollRootRef: drawerScrollRef,
    loadMoreSentinelRef: drawerLoadMoreSentinelRef,
    skipInitialLoadIfPopulated: true,
  });

  const handlePick = (asset: { id: string }) => {
    pickAssetFromDrawer(asset.id);
    onClose();
  };

  const loadStatusText = libDrawerPortraitLoading
    ? '加载中…'
    : libDrawerRemoteAssets.length
      ? libDrawerPortraitHasMore
        ? `已加载 ${libDrawerRemoteAssets.length} 个，继续向下滚动加载更多`
        : `共 ${libDrawerRemoteAssets.length} 个素材`
      : '暂无素材';

  const searchPrefix = libDrawerSearchName.trim()
    ? `搜索「${libDrawerSearchName.trim()}」：`
    : '';

  return (
    <>
      <div className={`drawer-overlay${open ? ' show' : ''}`} onClick={onClose} />
      <div className={`lib-drawer${open ? ' open' : ''}`}>
        <div className="lib-drawer-header">
          <span className="lib-drawer-title">📦 素材库</span>
          <button className="lib-drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="lib-drawer-group-bar">
          <VirtualAvatarGroupSwitcher active={open} />
        </div>
        {libSelectedGroupId ? (
          <div className="lib-drawer-search">
            <div className="lib-name-search-field">
              <input
                className="lib-name-search-input"
                type="search"
                placeholder="按素材名称搜索…"
                value={nameSearchInput}
                onChange={(e) => setNameSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitNameSearch();
                }}
                aria-label="按素材名称搜索"
              />
              {nameSearchInput.trim() ? (
                <button
                  type="button"
                  className="lib-name-search-clear"
                  onClick={clearNameSearch}
                  aria-label="清除搜索"
                >
                  ✕
                </button>
              ) : null}
              <button type="button" className="lib-name-search-btn" onClick={submitNameSearch}>
                搜索
              </button>
            </div>
          </div>
        ) : null}
        <div className="lib-drawer-body" ref={drawerScrollRef}>
          {libSelectedGroupId ? (
            <>
              <p className="lib-drawer-count" role="status" aria-live="polite">
                {libDrawerSearchName.trim() ? `${searchPrefix}` : ''}{loadStatusText}
              </p>
              <div className="lib-drawer-grid">
                <div
                  className="lib-drawer-upload-btn"
                  onClick={() => drawerUploadRef.current?.click()}
                >
                  <span className="lib-drawer-upload-ico" aria-hidden>
                    {libUploading ? '⏳' : '📤'}
                  </span>
                  <span>{libUploading ? '上传中…' : '上传图片'}</span>
                </div>
                <input
                  type="file"
                  multiple
                  accept={`image/*,video/*,.mp4,.mov,.webm,${LIBRARY_AUDIO_ACCEPT}`}
                  ref={drawerUploadRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files) uploadPortraitsToSelectedGroup(e.target);
                  }}
                />
                {libDrawerRemoteAssets.map((asset) =>
                  asset.type === 'video' ? (
                    <VideoAssetCard
                      key={asset.id}
                      variant="mini"
                      staticThumbnail
                      videoUrl={asset.url}
                      poster={asset.thumb}
                      title={asset.name}
                      onClick={() => handlePick(asset)}
                      deleteAction={{
                        onClick: () => requestLibraryAssetDelete(asset),
                      }}
                    />
                  ) : asset.type === 'audio' ? (
                    <AudioAssetCard
                      key={asset.id}
                      variant="mini"
                      audioUrl={asset.url}
                      title={asset.name}
                      onClick={() => handlePick(asset)}
                      deleteAction={{
                        onClick: () => requestLibraryAssetDelete(asset),
                      }}
                    />
                  ) : (
                    <AssetCard
                      key={asset.id}
                      variant="mini"
                      src={asset.thumb || asset.url}
                      title={asset.name}
                      onClick={() => handlePick(asset)}
                      deleteAction={{
                        onClick: () => requestLibraryAssetDelete(asset),
                      }}
                    />
                  ),
                )}
              </div>
              {libDrawerRemoteAssets.length > 0 ? (
                <div
                  ref={drawerLoadMoreSentinelRef}
                  className="lib-drawer-load-more"
                  role="status"
                  aria-live="polite"
                >
                  {libDrawerPortraitLoadingMore ? '加载更多…' : libDrawerPortraitHasMore ? '' : '已加载全部'}
                </div>
              ) : libDrawerPortraitLoading ? (
                <p className="lib-drawer-empty-hint">正在加载素材…</p>
              ) : libDrawerSearchName.trim() ? (
                <p className="lib-drawer-empty-hint">
                  未找到名称含「{libDrawerSearchName.trim()}」的素材
                </p>
              ) : (
                <p className="lib-drawer-empty-hint">当前组暂无素材，点击上传卡片添加</p>
              )}
            </>
          ) : (
            <div className="lib-drawer-empty-hint lib-drawer-empty-hint--center">
              请选择上方素材组，或先在「素材库」页配置系统访问令牌
            </div>
          )}
        </div>
      </div>
    </>
  );
};
